<template>
    <div class="container">
        <span @click="$emit('close')" role="button"><small><i
                    class="fa-solid fa-chevron-left me-2"></i>{{ $t('common.back') }}</small></span>
        <h2 class="my-3">{{ $t('modal.toCustomer') }}</h2>

        <div class="mb-3 text-multiline text-break">
            <label class="form-label"><small>{{ $t('taskDetail.workDone') }}</small></label>
            <div class="bg-white p-3 small">{{ this.notes2 }}</div>
        </div>

        <!-- Rendered from whatever the server says the PDF will contain, rather than
             from local task data: the customer must see what they are signing, and a
             server-side rule change has to reach this screen without an app release.
             Columns and cells are both data, so nothing here knows what a block is. -->
        <div v-if="previewError" class="mb-3 text-danger small">{{ $t('modal.signPreviewFailed') }}</div>
        <div v-else-if="previewLoading" class="mb-3 fst-italic small">{{ $t('common.loading') }}</div>
        <div v-else v-for="block in previewBlocks" :key="block.key" class="mb-3">
            <div class="table-responsive">
                <table class="table table-sm small bg-white mb-0">
                    <thead>
                        <tr>
                            <th v-for="(column, i) in block.columns" :key="i"
                                :class="{ 'text-end': column.align === 'right' }">{{ column.label }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, r) in block.rows" :key="r">
                            <td v-for="(cell, c) in row" :key="c" class="text-break"
                                :class="{ 'text-end': block.columns[c] && block.columns[c].align === 'right' }">{{ cell }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div class="mb-3 form-check">
            <input type="checkbox" v-model="isNotSigned" class="form-check-input" id="signedCheckbox">
            <label class="form-check-label small" for="signedCheckbox">{{ $t('modal.noCustomerSignature') }}</label>
        </div>
        <div class="mb-3">
            <label class="form-label"><small>{{ $t('modal.customerSignature') }}</small></label>
            <vue-signature-pad class="bg-white signature-pad" ref="signaturePadCustomer"
                :options="signaturePadOptions"></vue-signature-pad>
        </div>
        <button class="btn btn-sm border-1 border-primary text-primary" @click="clearSignatureCustomer">{{ $t('modal.clear') }}</button>
        <div class="my-3">
            <label class="form-label"><small>{{ $t('modal.technicianSignature') }}</small></label>
            <vue-signature-pad class="bg-white signature-pad" ref="signaturePadUser"
                :options="signaturePadOptions"></vue-signature-pad>
        </div>
        <button class="btn btn-sm border-1 border-primary text-primary" @click="clearSignatureUser">{{ $t('modal.clear') }}</button>
        <div class="my-3 form-check">
            <input type="checkbox" v-model="saveSignature" class="form-check-input" id="saveSignatureCheckbox">
            <label class="form-check-label small" for="saveSignatureCheckbox">{{ $t('modal.saveSignature') }}</label>
        </div>
        <div class="row mt-3">
            <div class="col">
                <button @click="$emit('close')" class="btn border-1 border-primary text-primary w-100" :disabled="actionInProgress">{{ $t('common.cancel') }}</button>
            </div>
            <div class="col">
                <button @click="submitSignature" class="btn btn-primary w-100" :disabled="actionInProgress">{{ $t('modal.sign') }}</button>
            </div>
        </div>
    </div>
</template>

<script>

import axios from '@/services/axios';
import { VueSignaturePad } from 'vue-signature-pad';

// vue-signature-pad resizes the canvas on every window resize and replays the
// captured strokes with fromData() — but those points keep the coordinates they
// were drawn at. Rotating from landscape to portrait therefore shrinks the pad
// while the signature stays its original size, so it runs off the edge.
//
// Replacing the library's handler lets us scale the stroke coordinates by the
// same factor as the canvas. `this` is the vue-signature-pad component instance;
// the library binds it when it takes options.resizeHandler.
function resizeAndRescale() {
    const canvas = this.$refs.signaturePadCanvas;
    const pad = this.signaturePad;
    const ratio = this.scaleToDevicePixelRatio ? Math.max(window.devicePixelRatio || 1, 1) : 1;

    // The backing store still describes the pre-resize canvas, which is the size
    // the existing strokes were drawn against.
    const previousWidth = canvas.width / ratio;
    const strokes = pad.toData();
    // A signature restored with fromDataURL — the technician's saved one, loaded
    // on mount — leaves no point data behind, so it needs a bitmap copy or the
    // resize would wipe it. Must be read before the canvas is resized.
    const bitmap = (!strokes.length && !pad.isEmpty()) ? canvas.toDataURL() : null;

    const width = canvas.offsetWidth;
    const height = canvas.offsetHeight;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.getContext('2d').scale(ratio, ratio);
    pad.clear();

    // The pad has a fixed aspect ratio, so width and height scale together and a
    // single factor is enough.
    const scale = previousWidth > 0 ? width / previousWidth : 1;

    if (strokes.length) {
        pad.fromData(scale === 1 ? strokes : strokes.map(group => ({
            ...group,
            points: group.points.map(point => ({
                ...point,
                x: point.x * scale,
                y: point.y * scale
            }))
        })));
    } else if (bitmap) {
        pad.fromDataURL(bitmap, { ratio, width, height });
    }
}

// Frozen and module-level so the component's `options` watcher never sees a new
// object on re-render.
const SIGNATURE_PAD_OPTIONS = Object.freeze({ resizeHandler: resizeAndRescale });

export default {
    components: {
        VueSignaturePad,
    },
    props: {
        taskId: {
            type: Number,
            required: true
        },
        notes2: {
            type: String,
            required: true
        },
    },
    data() {
        return {
            isNotSigned: false,
            saveSignature: false,
            previewBlocks: [],
            previewLoading: true,
            previewError: false,
            // Every call creates another signed PDF, and the two normalizeSignature
            // image decodes below finish before the request is even sent — a wide
            // window to click into.
            actionInProgress: false,
        };
    },
    methods: {
        async submitSignature() {
            if (this.actionInProgress) return;
            this.actionInProgress = true;
            try {
                const customerSignatureData = await this.normalizeSignature(this.$refs.signaturePadCustomer.saveSignature());
                const userSignatureData = await this.normalizeSignature(this.$refs.signaturePadUser.saveSignature());
                const payload = {
                    notes2: this.notes2,
                    signatureCustomer: customerSignatureData,
                    signatureUser: userSignatureData,
                    isSigned: !this.isNotSigned,
                    saveSignature: this.saveSignature
                };

                const response = await axios.post(`/tasks/${this.taskId}/sign`, payload);

                if (response.status === 200) {
                    this.$emit('signature-added');
                    this.$emit('close');
                }
            } catch (error) {
                console.error('Error submitting signature:', error);
            } finally {
                this.actionInProgress = false;
            }
        },
        // Redraw a captured signature onto a fixed-size canvas so every stored
        // signature has consistent dimensions regardless of screen width / device pixel ratio.
        // Aspect ratio is preserved (letterboxed, centered) to avoid distortion.
        normalizeSignature(result) {
            const TARGET_WIDTH = 450;
            const TARGET_HEIGHT = 141; // ~3.2:1 landscape frame suited to signatures
            return new Promise((resolve) => {
                if (!result || result.isEmpty || !result.data) {
                    resolve(result);
                    return;
                }
                const canvas = document.createElement('canvas');
                canvas.width = TARGET_WIDTH;
                canvas.height = TARGET_HEIGHT;
                const ctx = canvas.getContext('2d');
                const image = new Image();
                image.onload = () => {
                    const scale = Math.min(TARGET_WIDTH / image.width, TARGET_HEIGHT / image.height);
                    const drawWidth = image.width * scale;
                    const drawHeight = image.height * scale;
                    const dx = (TARGET_WIDTH - drawWidth) / 2;
                    const dy = (TARGET_HEIGHT - drawHeight) / 2;
                    ctx.drawImage(image, dx, dy, drawWidth, drawHeight);
                    resolve({ ...result, data: canvas.toDataURL('image/png') });
                };
                image.onerror = () => resolve(result);
                image.src = result.data;
            });
        },
        // Deliberately no fallback to locally held resources on failure: showing
        // something that differs from the PDF is exactly the bug this replaced.
        async loadSignPreview() {
            this.previewLoading = true;
            this.previewError = false;
            try {
                const response = await axios.get(`/tasks/${this.taskId}/sign-preview`);
                this.previewBlocks = response.data?.data?.blocks || [];
            } catch (error) {
                console.error('Error loading sign preview:', error);
                this.previewError = true;
            } finally {
                this.previewLoading = false;
            }
        },
        async loadUserSignature() {
            try {
                const response = await axios.get(`/users/${this.currentUser.id}/signature`);
                if (response.status === 200 && response.data?.data) {
                    // Load the saved signature into the signature pad
                    this.$refs.signaturePadUser.fromDataURL(response.data.data);
                }
            } catch (error) {
                console.error('Error loading user signature:', error);
            }
        },
        clearSignatureCustomer() {
            this.$refs.signaturePadCustomer.undoSignature();
        },
        clearSignatureUser() {
            this.$refs.signaturePadUser.undoSignature();
        }
    },
    mounted() {
        this.loadUserSignature();
        this.loadSignPreview();
    },
    computed: {
        signaturePadOptions() {
            return SIGNATURE_PAD_OPTIONS;
        },
        currentUser() {
            const user = localStorage.getItem('user');
            return user ? JSON.parse(user) : null;
        }
    },
}
</script>

<style scoped>
.signature-pad {
    width: 100%;
    max-width: 600px;
    height: auto !important;
    aspect-ratio: 16 / 5; /* 3.2:1 — matches the stored 800x250 frame, so no letterboxing */
    border: 1px solid #dee2e6;
}
</style>
